import React, { useEffect, useState } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';

import { AuthManagerContext, AuthManagerContextProvider, defaultAuthManagerValue } from '@private-crm-app/auth-manager-context';
import { CRM_TOKEN } from '@private-crm-app/constants';
import PrivateSubmissionAppContainer from '@private-crm-app/PrivateSubmissionApp/PrivateSubmissionAppContainer';
import { syncCrmTokenSession } from '@private-crm-app/syncCrmTokenSession';
import { UserContextProvider } from '@private-crm-app/user-context';
import { clearCrmTokenSession } from '@private-crm-app/utils';
import Loading from '@components/Loading';

import './style.scss';

// Lightweight CRM token validator for non-secure origins where Auth0Provider
// cannot initialise. Replicates the token validation that AuthManagerContextProvider
// normally performs so that agencyId is available for downstream flag loading.
function CRMTokenValidator({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const validate = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const crmToken = params.get(CRM_TOKEN);

        // Always re-validate when crm_token is present so agency_name stays canonical
        // for returning sessions (same token previously skipped validation and kept stale values).
        if (crmToken) {
          const result = await syncCrmTokenSession(crmToken);
          if (!result.ok) {
            clearCrmTokenSession();
          }
        }
      } catch (error) {
        console.error('CRM token validation failed:', error);
        clearCrmTokenSession();
      } finally {
        setReady(true);
      }
    };

    validate();
  }, []);

  if (!ready) {
    return <Loading show />;
  }

  return <>{children}</>;
}

export default function PrivateSubmissionApp() {
  const pageShowHandler = (event: PageTransitionEvent) => {
    if (event.persisted) {
      location.reload(); // Optional: Force a reload
    }
  };

  useEffect(() => {
    window.addEventListener('pageshow', pageShowHandler);

    return () => {
      window.removeEventListener('pageshow', pageShowHandler);
    };
  }, []);

  // Auth0 SPA JS requires a secure origin (HTTPS or localhost).
  // On non-secure origins (e.g. LAN IP for mobile testing), bypass Auth0Provider
  // and provide default unauthenticated context so public pages still render.
  // Requires REACT_APP_ALLOW_HTTP_AUTH_FALLBACK=true to activate, preventing
  // accidental auth bypass in production. Only set this in development environments.
  if (!window.isSecureContext && process.env.REACT_APP_ALLOW_HTTP_AUTH_FALLBACK === 'true' && process.env.NODE_ENV !== 'production') {
    return (
      <CRMTokenValidator>
        <AuthManagerContext.Provider value={{ ...defaultAuthManagerValue, isAuthResolved: true }}>
          <UserContextProvider>
            <PrivateSubmissionAppContainer />
          </UserContextProvider>
        </AuthManagerContext.Provider>
      </CRMTokenValidator>
    );
  }

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      useRefreshTokens
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: `${window.location.origin}/private-submission/listing`,
        scope: 'openid profile email phone address', // Include the phone in the JWT having the correct scope of info
      }}
    >
      <AuthManagerContextProvider>
        <UserContextProvider>
          <PrivateSubmissionAppContainer />
        </UserContextProvider>
      </AuthManagerContextProvider>
    </Auth0Provider>
  );
}
